import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Form,
  Spinner,
  Card,
  Modal,
  Pagination,
  Badge,
} from "react-bootstrap";
import { Eye, Pencil, Trash, Plus } from "react-bootstrap-icons";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://34.206.193.218:4001/api/admin";
const ITEMS_PER_PAGE = 5;

const Task = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);

  const [showView, setShowView] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const [selectedTask, setSelectedTask] = useState(null);
  const [viewTask, setViewTask] = useState(null);

  const [newTask, setNewTask] = useState({
    taskName: "",
    actualCost: "",
  });

  const token =
    localStorage.getItem("adminToken") || localStorage.getItem("token");

  /* ================= FETCH TASKS ================= */
  const fetchTasks = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/admin/tasks/allTasks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setTasks(res.data.data);
        setCurrentPage(1);
      }
    } catch {
      toast.error("Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(tasks.length / ITEMS_PER_PAGE);
  const paginatedTasks = tasks.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  /* ================= VIEW TASK ================= */
  const viewTaskDetails = async (id) => {
    try {
      const res = await axios.get(
        `${API_BASE}/admin/tasks/getById/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setViewTask(res.data.data);
      setShowView(true);
    } catch {
      toast.error("Failed to load task");
    }
  };

  /* ================= CREATE ================= */
  const createTask = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(
        `${API_BASE}/admin/tasks/createTask`,
        newTask,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTasks((prev) => [res.data.data, ...prev]);

      toast.success("Task created");
      setShowCreate(false);
      setNewTask({ taskName: "", actualCost: "" });
    } catch {
      toast.error("Failed to create task");
    }
  };

  /* ================= UPDATE (NAME + COST + HISTORY) ================= */
  const updateTask = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.put(
        `${API_BASE}/admin/tasks/updateTask/${selectedTask._id}`,
        {
          taskName: selectedTask.taskName,
          actualCost: selectedTask.actualCost,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTasks((prev) =>
        prev.map((t) =>
          t._id === selectedTask._id ? res.data.data : t
        )
      );

      toast.success("Task updated");
      setShowEdit(false);
    } catch {
      toast.error("Failed to update task");
    }
  };

  /* ================= DELETE ================= */
  const deleteTask = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await axios.delete(
        `${API_BASE}/admin/tasks/deleteTask/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTasks((prev) => prev.filter((t) => t._id !== id));
      toast.success("Task deleted");
    } catch {
      toast.error("Failed to delete task");
    }
  };

  return (
    <div>
      <ToastContainer />

      <Card className="border-0">
        <Card.Body className="p-0">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4 className="fw-bold mb-0">Bid Estimate Tasks</h4>
              <small className="text-muted">
                Total Tasks: <Badge bg="secondary">{tasks.length}</Badge>
              </small>
            </div>
            <Button onClick={() => setShowCreate(true)}>
              <Plus /> Add Task
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : (
            <>
              <Table hover responsive className="align-middle">
                <thead className="table-dark">
                  <tr>
                    <th>Task Name</th>
                    <th>Actual Cost</th>
                    <th>Created At</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTasks.map((task) => (
                    <tr key={task._id}>
                      <td className="fw-semibold">{task.taskName}</td>
                      <td>$ {task.actualCost}</td>
                      <td>
                        {new Date(task.createdAt).toLocaleDateString()}
                      </td>
                      <td className="text-center d-flex gap-2 justify-content-center">
                        <Button size="sm" onClick={() => viewTaskDetails(task._id)}>
                          <Eye />
                        </Button>
                        <Button
                          size="sm"
                          variant="warning"
                          onClick={() => {
                            setSelectedTask(task);
                            setShowEdit(true);
                          }}
                        >
                          <Pencil />
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => deleteTask(task._id)}
                        >
                          <Trash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <Pagination className="justify-content-end">
                  {[...Array(totalPages)].map((_, i) => (
                    <Pagination.Item
                      key={i}
                      active={i + 1 === currentPage}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </Pagination.Item>
                  ))}
                </Pagination>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* ===== VIEW MODAL (WITH HISTORY) ===== */}
      <Modal show={showView} onHide={() => setShowView(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Task Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewTask && (
            <>
              <p><strong>Name:</strong> {viewTask.taskName}</p>
              <p><strong>Current Cost:</strong> $ {viewTask.actualCost}</p>

              <hr />
              <h6>Cost Update History</h6>

              {viewTask.costHistory?.length ? (
                viewTask.costHistory.map((h, i) => (
                  <div key={i} className="d-flex justify-content-between">
                    <span>$ {h.amount}</span>
                    <small className="text-muted">
                      {new Date(h.updatedAt).toLocaleString()}
                    </small>
                  </div>
                ))
              ) : (
                <small className="text-muted">No updates yet</small>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* ===== CREATE MODAL ===== */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={createTask}>
            <Form.Control
              className="mb-3"
              placeholder="Task Name"
              value={newTask.taskName}
              onChange={(e) =>
                setNewTask({ ...newTask, taskName: e.target.value })
              }
              required
            />
            <Form.Control
              type="number"
              placeholder="Actual Cost"
              value={newTask.actualCost}
              onChange={(e) =>
                setNewTask({ ...newTask, actualCost: e.target.value })
              }
              required
            />
            <Button type="submit" className="w-100 mt-3">
              Create
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ===== EDIT MODAL (NAME + COST) ===== */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTask && (
            <Form onSubmit={updateTask}>
              <Form.Control
                className="mb-3"
                value={selectedTask.taskName}
                onChange={(e) =>
                  setSelectedTask({
                    ...selectedTask,
                    taskName: e.target.value,
                  })
                }
                required
              />
              <Form.Control
                type="number"
                value={selectedTask.actualCost}
                onChange={(e) =>
                  setSelectedTask({
                    ...selectedTask,
                    actualCost: e.target.value,
                  })
                }
                required
              />
              <Button type="submit" className="w-100 mt-3">
                Update
              </Button>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Task;
